import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Message } from '@worksy360/shared';
import { useCustomers, useInbox } from '../lib/hooks.js';
import { Badge, Button, Card, EmptyState, Skeleton } from '../ui/index.js';
import { MessageThread } from '../components/MessageThread.js';
import { relTime } from '../lib/format.js';

export function Inbox() {
  const [state, setState] = useState<'open' | 'all'>('open');
  const { data, isLoading } = useInbox(state);
  const customers = useCustomers();
  const [selected, setSelected] = useState<string | null>(null);

  const nameOf = useMemo(() => {
    const m = new Map((customers.data?.items ?? []).map((c) => [c.id, c.displayName]));
    return (id: string) => m.get(id) ?? 'Customer';
  }, [customers.data]);

  // Group inbox messages into one conversation per customer (latest message wins).
  const convos = useMemo(() => {
    const byCust = new Map<string, Message>();
    for (const m of data?.items ?? []) {
      const cur = byCust.get(m.customerId);
      if (!cur || m.createdAt > cur.createdAt) byCust.set(m.customerId, m);
    }
    return [...byCust.values()].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [data]);

  const activeCid = selected ?? convos[0]?.customerId ?? null;

  return (
    <>
      <div className="page-head">
        <div><div className="page-title">Inbox</div><div className="page-sub">Calls, texts & email in one place</div></div>
        <div className="row gap-2">
          <button className={`btn ${state === 'open' ? 'btn-primary' : 'btn-secondary'} btn-sm`} onClick={() => setState('open')}>Needs response</button>
          <button className={`btn ${state === 'all' ? 'btn-primary' : 'btn-secondary'} btn-sm`} onClick={() => setState('all')}>All</button>
        </div>
      </div>

      <div className="inbox">
        <Card>
          {isLoading ? (
            <div className="card-pad stack gap-3">{[0, 1, 2].map((i) => <Skeleton key={i} h={40} />)}</div>
          ) : convos.length === 0 ? (
            <EmptyState icon="☏" title={state === 'open' ? 'Inbox zero 🎉' : 'No conversations yet'}>
              Texts from customers show up here. Try “Text” on a customer, or simulate an inbound message from their page.
            </EmptyState>
          ) : convos.map((m) => (
            <div key={m.customerId} className={`convo ${activeCid === m.customerId ? 'active' : ''} ${m.needsResponse ? 'unread' : ''}`} onClick={() => setSelected(m.customerId)}>
              <div className="row between"><strong style={{ fontSize: 'var(--text-sm)' }}>{nameOf(m.customerId)}</strong><span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>{relTime(m.createdAt)}</span></div>
              <div className="row gap-2"><Badge tone={m.channel === 'call' ? 'neutral' : m.channel === 'note' ? 'warning' : 'info'}>{m.channel}</Badge><span className="muted" style={{ fontSize: 'var(--text-xs)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.body || '(no text)'}</span></div>
            </div>
          ))}
        </Card>

        {activeCid ? (
          <Card>
            <div className="card-head">
              <span className="card-title">{nameOf(activeCid)}</span>
              <Link to={`/customers/${activeCid}`}><Button size="sm" variant="secondary">Open customer →</Button></Link>
            </div>
            <MessageThread customerId={activeCid} />
          </Card>
        ) : <Card><EmptyState icon="☏" title="Select a conversation" /></Card>}
      </div>
    </>
  );
}
